/**
 * 404 Not Found Page
 * Premium iOS-style error page with smooth animations
 * 
 * @author Varun Pandey
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, ArrowLeft } from 'lucide-react';
import { Button } from '../../components/ui/Button';

// iOS-optimized easing
const iosEasing: [number, number, number, number] = [0.25, 0.1, 0.25, 1.0];

// Stagger animation for content
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: iosEasing,
    },
  },
};

export const NotFoundPage: React.FC = () => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate('/');
  };

  const handleGoBack = () => {
    // Check if there's history to go back to
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0D] flex flex-col items-center justify-center px-6 py-12 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Subtle gradient orbs */}
        <motion.div
          className="absolute top-1/4 -left-32 w-64 h-64 rounded-full bg-[#F33363]/5 blur-3xl"
          animate={{
            x: [0, 20, 0],
            y: [0, -20, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          className="absolute bottom-1/4 -right-32 w-64 h-64 rounded-full bg-[#F33363]/5 blur-3xl"
          animate={{
            x: [0, -20, 0],
            y: [0, 20, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1,
          }}
        />
      </div>

      <motion.div
        className="flex flex-col items-center text-center max-w-md relative z-10"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Logo */}
        <motion.div variants={itemVariants} className="mb-8">

        </motion.div>

        {/* 404 Number with floating animation */}
        <motion.div
          variants={itemVariants}
          className="relative mb-6"
        >
          <motion.div
            // variants={floatVariants}
            animate="animate"
            className="relative"
          >
            {/* Background glow */}
            <div className="absolute inset-0 text-[120px] sm:text-[150px] font-bold text-[#F33363]/10 blur-xl select-none">
              404
            </div>
            {/* Main number */}
            <h1 className="text-[120px] sm:text-[150px] font-bold text-transparent bg-clip-text bg-gradient-to-b from-white to-[#8E8E93] leading-none select-none">
              404
            </h1>
          </motion.div>

          {/* Floating search icon */}

        </motion.div>

        {/* Title */}
        <motion.h2
          variants={itemVariants}
          className="text-2xl sm:text-3xl font-bold text-white mb-3"
        >
          Page Not Found
        </motion.h2>

        {/* Description */}
        <motion.p
          variants={itemVariants}
          className="text-[#8E8E93] text-base sm:text-lg mb-8 leading-relaxed"
        >
          The page you're looking for doesn't exist or has been moved.
          Let's get you back on track.
        </motion.p>

        {/* Action buttons */}
        <motion.div
          variants={itemVariants}
          className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto"
        >
          <Button
            variant="active"
            onClick={handleGoHome}
            className="w-full sm:w-auto px-8"
          >
            <Home className="w-4 h-4 mr-2" />
            Go Home
          </Button>

          <button
            onClick={handleGoBack}
            className="flex items-center justify-center gap-2 px-8 py-3 rounded-[0.75rem] bg-[#2C2C2E] text-white font-semibold text-sm hover:bg-[#3A3A3C] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Go Back
          </button>
        </motion.div>

        {/* Helpful links */}
        <motion.div
          variants={itemVariants}
          className="mt-12 pt-8 border-t border-[#2C2C2E] w-full"
        >
          <p className="text-[#8E8E93] text-sm mb-4">Or try these helpful links:</p>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => navigate('/login')}
              className="text-sm text-[#F33363] hover:text-[#D92D58] transition-colors"
            >
              Sign In
            </button>
            <span className="text-[#3A3A3C]">•</span>
            <button
              onClick={() => navigate('/signup')}
              className="text-sm text-[#F33363] hover:text-[#D92D58] transition-colors"
            >
              Create Account
            </button>
            <span className="text-[#3A3A3C]">•</span>
            <button
              onClick={() => navigate('/dashboard')}
              className="text-sm text-[#F33363] hover:text-[#D92D58] transition-colors"
            >
              Dashboard
            </button>
          </div>
        </motion.div>
      </motion.div>

      {/* Bottom branding */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8, duration: 0.5 }}
        className="absolute bottom-6 text-[#3A3A3C] text-xs"
      >
        StarClinch © {new Date().getFullYear()}
      </motion.p>
    </div>
  );
};
